import { Link } from 'react-router';
import { APP_NAME, SUPPORT_EMAIL } from '@/lib/constants';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/" className="text-primary font-bold text-xl">{APP_NAME}</Link>
          <Link to="/" className="text-sm text-text-secondary hover:text-text transition-colors">Back to Home</Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-8 p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <p className="text-sm text-text-secondary">
            <strong>Note:</strong> These terms are a template and must be reviewed by a qualified legal professional before the platform goes live.
          </p>
        </div>

        <p className="text-sm text-text-secondary">Last updated: July 2026</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-text mt-2">Terms and Conditions</h1>

        <section className="mt-8 space-y-8 prose prose-gray max-w-none">
          <div>
            <h2 className="text-xl font-semibold">1. Service Description</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              {APP_NAME} (&quot;the Platform&quot;) is an independent college admission guidance and counselling service.
              We provide information, guidance, and counselling to help students identify suitable college programmes
              and navigate the admission process. We are not an official website of any government body, university,
              or college.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">2. No Admission Guarantee</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              Submitting an application through our platform does not guarantee admission to any college or university.
              Admission decisions are made solely by the respective educational institutions based on their own criteria,
              availability of seats, and applicable regulations. We do not influence or control admission outcomes.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">3. Accuracy of Information</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              You are responsible for ensuring that all information and documents you submit through the Platform
              are accurate, genuine, and complete. Providing false or misleading information may affect your
              admission prospects and may result in rejection or cancellation of admission by the institution.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">4. User Responsibility</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              As a user of the Platform, you agree to:
            </p>
            <ul className="mt-2 space-y-1 text-text-secondary">
              <li>Provide accurate and truthful information</li>
              <li>Upload genuine and unaltered documents</li>
              <li>Verify your phone number using the OTP process</li>
              <li>Respond to counsellor communications in a timely manner</li>
              <li>Use the Platform only for lawful purposes</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold">5. Communication Consent</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              By submitting an application, you consent to being contacted by our counsellors through phone,
              WhatsApp, or email regarding college admission guidance. Standard communication charges from
              your service provider may apply.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">6. Document Usage</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              Documents uploaded to the Platform are used solely for the purpose of assessing your eligibility
              and providing admission guidance. Your documents are stored securely and are not shared with
              third parties without your consent, except as required for the admission guidance process.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">7. Prohibited Usage</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              You agree not to:
            </p>
            <ul className="mt-2 space-y-1 text-text-secondary">
              <li>Submit false, fraudulent, or misleading applications</li>
              <li>Upload documents that do not belong to you</li>
              <li>Use automated tools or bots to interact with the Platform</li>
              <li>Attempt to access other users&apos; applications or documents</li>
              <li>Use the Platform for any unlawful purpose</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold">8. Service Availability</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              We strive to keep the Platform available at all times but do not guarantee uninterrupted access.
              The Platform may be temporarily unavailable for maintenance, updates, or reasons beyond our control.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">9. Limitation of Liability</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              To the fullest extent permitted by applicable law, {APP_NAME} and its team members shall not be
              liable for any indirect, incidental, or consequential damages arising from your use of the Platform,
              including but not limited to admission rejection, missed deadlines, or any decisions made by
              educational institutions.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">10. Changes to Terms</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              We may update these Terms from time to time. Continued use of the Platform after changes
              constitutes acceptance of the updated Terms. We will notify users of material changes through
              the Platform or via email.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold">11. Contact</h2>
            <p className="mt-2 text-text-secondary leading-relaxed">
              For questions about these Terms, contact us at{' '}
              <span className="font-medium">{SUPPORT_EMAIL}</span> or visit our{' '}
              <Link to="/contact" className="text-primary hover:text-primary-dark underline">Contact page</Link>.
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto max-w-3xl px-4 py-6 text-center text-xs text-text-secondary">
          <p>&copy; {new Date().getFullYear()} {APP_NAME}. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
